import * as THREE from 'three';
import type { DxfLayer, DxfTextLabel } from '../types';
import { aciToHex } from './dxf';
import {
  getOrthoPixelsPerWorldUnit,
  getOrthoWorldView,
  getPerspectiveGroundView,
  getPerspectivePixelsPerWorldUnit,
} from './flatPlanCamera';

/** Below this on-screen cap height (px) labels are unreadable — matches 2D FloorPlanLabels. */
export const MIN_FLOOR_LABEL_PX = 6;

export const DEFAULT_FLOOR_LABEL_COLOR = '#334155';

/** Above DXF floor lines (y = 0.5) so labels are not depth-fighting. */
export const FLOOR_LABEL_ELEVATION = 2;

export function estimateFloorLabelWidth(text: string, capHeight: number): number {
  return text.length * capHeight * 0.7;
}

/** Skip 1–2 char grid refs (column letters / bay numbers) that clutter equipment icons. */
export function isShortGridFloorLabel(text: string): boolean {
  return /^[A-Za-z0-9]{1,2}$/.test(text.trim());
}

export function resolveFloorPlanLabelColor(
  label: DxfTextLabel,
  layers: DxfLayer[],
  defaultColor = DEFAULT_FLOOR_LABEL_COLOR
): string {
  const own = aciToHex(label.color);
  if (own) return own;
  const layer = layers[label.layerIndex];
  return (layer ? aciToHex(layer.color) : null) ?? defaultColor;
}

/** Konva world-space view bounds (same math as FloorPlanLabels). */
export function getKonvaWorldViewBounds(
  panX: number,
  panY: number,
  zoom: number,
  canvasWidth: number,
  canvasHeight: number
): { minX: number; maxX: number; minY: number; maxY: number } {
  const scale = zoom || 1;
  const minX = -panX / scale;
  const minY = -panY / scale;
  return {
    minX,
    minY,
    maxX: minX + canvasWidth / scale,
    maxY: minY + canvasHeight / scale,
  };
}

export function isFloorLabelInWorldView(
  label: DxfTextLabel,
  view: { minX: number; maxX: number; minY: number; maxY: number }
): boolean {
  const h = label.height;
  const widthWorld = estimateFloorLabelWidth(label.text, h);
  return !(
    label.x + widthWorld < view.minX ||
    label.x - h > view.maxX ||
    label.y + h < view.minY ||
    label.y - h > view.maxY
  );
}

/** Canvas rotation (Y-down) → Three.js Y rotation for floor-aligned text. */
export function floorLabelRotationY(rotationDeg: number): number {
  return (rotationDeg * Math.PI) / 180;
}

export type FloorLabelCullInput = {
  labels: DxfTextLabel[];
  layers: DxfLayer[];
  camera: THREE.Camera;
  viewportWidth: number;
  viewportHeight: number;
  konvaZoom: number;
  konvaPanX: number;
  konvaPanY: number;
  viewCenterX: number;
  viewCenterZ: number;
};

/**
 * Same visibility rules as FloorPlanLabels.tsx.
 * Perspective 3D uses live camera projection for zoom-dependent label culling.
 */
export function computeVisibleFloorLabelIndices(input: FloorLabelCullInput): number[] {
  const {
    labels,
    camera,
    viewportWidth,
    viewportHeight,
    konvaZoom,
    konvaPanX,
    konvaPanY,
    viewCenterX,
    viewCenterZ,
  } = input;

  const indices: number[] = [];

  const ortho = camera instanceof THREE.OrthographicCamera;
  const perspective = camera instanceof THREE.PerspectiveCamera;

  const pixelsPerUnit = ortho
    ? getOrthoPixelsPerWorldUnit(camera, viewportHeight)
    : perspective
      ? getPerspectivePixelsPerWorldUnit(camera, viewportHeight, viewCenterX, viewCenterZ)
      : konvaZoom;

  const worldView = ortho
    ? getOrthoWorldView(camera, viewCenterX, viewCenterZ)
    : perspective
      ? getPerspectiveGroundView(camera, viewportWidth, viewportHeight)
      : getKonvaWorldViewBounds(konvaPanX, konvaPanY, konvaZoom, viewportWidth, viewportHeight);

  for (let i = 0; i < labels.length; i++) {
    const label = labels[i]!;
    if (isShortGridFloorLabel(label.text)) continue;
    const h = label.height;
    if (h <= 0) continue;

    // Identical to 2D: h * stageScale < MIN_LABEL_PX → skip
    if (h * pixelsPerUnit < MIN_FLOOR_LABEL_PX) continue;

    if (!isFloorLabelInWorldView(label, worldView)) continue;

    indices.push(i);
  }

  return indices;
}
